import { makeStyles } from 'tss-react/mui';
import React, { useCallback, useEffect, useState } from 'react';
import {
  alphanumericRegExp,
  Button,
  Grid,
  InputLabel,
  KeyValue,
  TextFieldForm,
  Typography,
  Link
} from 'web-library';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ContactNumberFormItem } from './form-items/ContactNumberFormItem';
import FormHeader from './form-items/FormHeader';
import NumberOnlyFormItem from './form-items/NumberOnlyFormItem';
import { fallbackEmptyString, prefix0For2Digits, stringIsEmpty } from '../utils/StringUtils';
import { getAddressDisplayArray, getContactDisplayValue } from '../utils/ComponentUtils';
import { ContactDetailsItem } from '../api/candidate/exam/model';
import LocalAddressHelper from './helper-text/LocalAddressHelper';
import { CandidateProfileFormData } from '../models';
import { useGetAddressDetailsByPostcode } from '../modules/accountManagement/logic/useGetAddressDetailsByPostcode';

const useStyles = makeStyles()((theme) =>
({
  container: {
    // marginBottom: theme.spacing(2),
  },
  iconButton: {
    padding: theme.spacing(1),
    margin: 0,
    marginLeft: theme.spacing(2),
  },
  root: {
    margin: theme.spacing(2),
  },
  formContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  address: {
    '& .MuiInputBase-root': {
      marginBottom: theme.spacing(1),
    },
    marginBottom: theme.spacing(1),
  },
  formElementContainer: {
    marginBottom: theme.spacing(2),
  },
  unitTextInput: {
    paddingLeft: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
    },
  },
  getAddressButton: {
    width: 'calc(100% - 166px)',
    paddingRight: theme.spacing(1),
    display: 'inline-flex',
    [theme.breakpoints.down('md')]: {
      width: '100%',
      paddingRight: theme.spacing(0),
    },
  },
}));

const useEmail = (contactDetails: ContactDetailsItem | undefined) => contactDetails?.email || '';
const usePhoneNumber = (contactDetails: ContactDetailsItem | undefined) =>
  getContactDisplayValue(
    false,
    contactDetails?.phoneNumber?.countryCode?.codeValueDescription,
    contactDetails?.phoneNumber?.number
  );
const useMobileNumber = (contactDetails: ContactDetailsItem | undefined) =>
  getContactDisplayValue(
    false,
    contactDetails?.mobileNumber?.countryCode.codeValueDescription,
    contactDetails?.mobileNumber?.number
  );
const useMailingAddress = (contactDetails: ContactDetailsItem | undefined) => {
  const displayTexts: string[] = [
    fallbackEmptyString(contactDetails?.mailingAddress?.houseOrBlockNumber),
    fallbackEmptyString(contactDetails?.mailingAddress?.streetName),
    fallbackEmptyString(contactDetails?.mailingAddress?.buildingName),
  ];

  let floorAndUnit: string = '';
  if (
    !stringIsEmpty(contactDetails?.mailingAddress?.floorNumber) &&
    !stringIsEmpty(contactDetails?.mailingAddress?.unitNumber)
  ) {
    floorAndUnit = `#${contactDetails?.mailingAddress?.floorNumber}-${contactDetails?.mailingAddress?.unitNumber}`;
  } else if (!stringIsEmpty(contactDetails?.mailingAddress?.floorNumber)) {
    floorAndUnit = `#${contactDetails?.mailingAddress?.floorNumber}`;
  } else if (!stringIsEmpty(contactDetails?.mailingAddress?.unitNumber)) {
    floorAndUnit = `-${contactDetails?.mailingAddress?.unitNumber}`;
  }
  displayTexts.push(floorAndUnit);

  if (!stringIsEmpty(contactDetails?.mailingAddress?.postalCode)) {
    displayTexts.push(`Singapore ${contactDetails?.mailingAddress?.postalCode}`);
  }

  return getAddressDisplayArray(displayTexts);
};
const useForeignAddress = (contactDetails: ContactDetailsItem | undefined) =>
  getAddressDisplayArray([
    contactDetails?.foreignAddress1,
    contactDetails?.foreignAddress2,
    contactDetails?.foreignAddress3,
  ]);

export interface ContactDetailsProps {
  contactDetails: ContactDetailsItem | undefined;
  editable: boolean;
  editMode?: boolean;
  onEdit?: (mode: boolean) => void;
  formHook?: UseFormReturn<any>;
}

const AddressEditSection = ({
  formHook,
  contactDetails,
}: {
  formHook: UseFormReturn<CandidateProfileFormData>;
  contactDetails: ContactDetailsItem | undefined;
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const {
    isDisableAddr,
    validatePostalCode,
    isApiError,
    isPostalCodeEmpty,
    postcodeOnChange,
  } = useGetAddressDetailsByPostcode<CandidateProfileFormData>({
    blkNoInputName: 'houseOrBlkNo',
    streetInputName: 'streetName',
    buildingInputName: 'building',
    postcodeInputName: 'postalCode',
    isPostcodeEmptyDefaultValue: contactDetails?.mailingAddress?.postalCode.length === 0,
    setValue: formHook?.setValue,
    getValues: formHook?.getValues,
    setError: formHook?.setError,
    clearErrors: formHook?.clearErrors,
    trigger: formHook?.trigger,
  });

  return (
    <>
      <InputLabel>{t('components->>ContactDetails.Residential Address')}</InputLabel>
      <LocalAddressHelper className={classes.address} />
      <Grid className={classes.address}>
        <TextFieldForm
          fullWidth
          isInputTextUpperCase
          variant="outlined"
          title={t('components->>ContactDetails.House/Block No.')}
          placeholder={t('components->>ContactDetails.House/Block No.')}
          name="houseOrBlkNo"
          error={!!formHook?.formState.errors?.houseOrBlkNo}
          helperText={formHook?.formState.errors?.houseOrBlkNo?.message?.toString()}
          defaultValue={contactDetails?.mailingAddress?.houseOrBlockNumber}
          form={formHook}
          inputProps={{ readOnly: isDisableAddr }}
        />
        <TextFieldForm
          fullWidth
          isInputTextUpperCase
          variant="outlined"
          title={t('components->>ContactDetails.Street Name')}
          placeholder={t('components->>ContactDetails.Street Name')}
          name="streetName"
          error={!!formHook?.formState.errors?.streetName}
          helperText={formHook?.formState.errors?.streetName?.message?.toString()}
          defaultValue={contactDetails?.mailingAddress?.streetName}
          form={formHook}
          inputProps={{ readOnly: isDisableAddr }}
        />
        <TextFieldForm
          fullWidth
          isInputTextUpperCase
          variant="outlined"
          id="building"
          name="building"
          title={t('components->>ContactDetails.Building')}
          placeholder={t('profile->>AddressHelper.BuildingPlaceholder')}
          error={!!formHook?.formState.errors?.building}
          helperText={formHook?.formState.errors?.building?.message?.toString()}
          defaultValue={contactDetails?.mailingAddress?.buildingName}
          form={formHook}
          inputProps={{ readOnly: isDisableAddr }}
        />

        <Grid container direction="row">
          <Grid xs={12} sm={12} md={6}>
            <NumberOnlyFormItem
              fullWidth
              variant="outlined"
              id="floor"
              name="floor"
              title={t('components->>ContactDetails.Floor')}
              placeholder={t('profile->>AddressHelper.FloorPlaceholder')}
              error={!!formHook?.formState.errors?.floor}
              helperText={formHook?.formState.errors?.floor?.message?.toString()}
              defaultValue={contactDetails?.mailingAddress?.floorNumber}
              form={formHook}
              startAdornment="#"
              onBlur={(e) => formHook.setValue('floor', prefix0For2Digits(e.target.value))}
            />
          </Grid>
          <Grid xs={12} sm={12} md={6}>
            <TextFieldForm
              className={classes.unitTextInput}
              fullWidth
              variant="outlined"
              id="unit"
              name="unit"
              title={t('components->>ContactDetails.Unit')}
              placeholder={t('profile->>AddressHelper.UnitPlaceholder')}
              defaultValue={contactDetails?.mailingAddress?.unitNumber}
              error={!!formHook?.formState.errors?.unit}
              helperText={formHook?.formState.errors?.unit?.message?.toString()}
              form={formHook}
              inputProps={{
                startAdornment: '-',
              }}
              regExpToBlock={alphanumericRegExp}
            />
          </Grid>
        </Grid>
        <Grid xs={12}>
          <NumberOnlyFormItem
            className={classes.getAddressButton}
            variant="outlined"
            id="postalCode"
            name="postalCode"
            title={t('components->>ContactDetails.Postal Code')}
            placeholder={t('components->>ContactDetails.Postal Code')}
            error={!!formHook?.formState.errors?.postalCode}
            helperText={formHook?.formState.errors?.postalCode?.message?.toString()}
            defaultValue={contactDetails?.mailingAddress?.postalCode}
            form={formHook}
            startAdornment="Singapore"
            onChange={(e) => postcodeOnChange(e.target.value)}
          />
          <Button
            id="button-validate-postcode"
            size="small"
            variant="outlined"
            onClick={validatePostalCode}
            disabled={isPostalCodeEmpty}
          >
            {t('profile->>CandidateProfileDetail.GetAddress')}
          </Button>
          {isApiError && (
            <Typography color="error">
              {t('profile->>CandidateProfileDetail.PostcodeApiErrorMsg')}
            </Typography>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const ForeignAddressEditSection = ({
  formHook,
  contactDetails,
}: {
  formHook: UseFormReturn<any>;
  contactDetails: ContactDetailsItem | undefined;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <TextFieldForm
        fullWidth
        isInputTextUpperCase
        onChange={(e) => formHook?.setValue('foreignAddress1', e.target.value.toUpperCase())}
        variant="outlined"
        label="Foreign Address"
        id="foreignAddress1"
        name="foreignAddress1"
        title="Address Line 1"
        placeholder="Address Line 1"
        error={!!formHook?.formState.errors?.foreignAddress1}
        helperText={formHook?.formState.errors?.foreignAddress1?.message?.toString()}
        defaultValue={contactDetails?.foreignAddress1}
        form={formHook}
        isSubLabelBelowLabel
        subLabel={
          <Typography variant="body2">{t('profile->>AddressHelper.ForeignAddress')}</Typography>
        }
      />
      <TextFieldForm
        fullWidth
        isInputTextUpperCase
        onChange={(e) => formHook?.setValue('foreignAddress2', e.target.value.toUpperCase())}
        variant="outlined"
        id="foreignAddress2"
        name="foreignAddress2"
        error={!!formHook?.formState.errors?.foreignAddress2}
        helperText={formHook?.formState.errors?.foreignAddress2?.message?.toString()}
        title={t('components:ContactDetails.Address Line 2')}
        placeholder={t('components:ContactDetails.Address Line 2')}
        defaultValue={contactDetails?.foreignAddress2}
        form={formHook}
      />
      <TextFieldForm
        fullWidth
        isInputTextUpperCase
        onChange={(e) => formHook?.setValue('foreignAddress3', e.target.value.toUpperCase())}
        variant="outlined"
        id="foreignAddress3"
        name="foreignAddress3"
        error={!!formHook?.formState.errors?.foreignAddress3}
        helperText={formHook?.formState.errors?.foreignAddress3?.message?.toString()}
        title={t('components:ContactDetails.Address Line 3')}
        placeholder={t('components:ContactDetails.Address Line 3')}
        defaultValue={contactDetails?.foreignAddress3}
        form={formHook}
      />
    </>
  );
};

interface NonEditableDetailsProps {
  emailVal: string;
  mobileVal: string;
  phoneVal: string;
  mailingAddressVal: string[];
  foreignAddressVal: string[];
}

const NonEditableDetails = ({
  emailVal,
  mobileVal,
  phoneVal,
  mailingAddressVal,
  foreignAddressVal,
}: NonEditableDetailsProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Grid xs={12} md={8}>
        <KeyValue
          id="email"
          keyText={t('components->>ContactDetails.Email')}
          valueText={emailVal}
        />
      </Grid>
      <Grid xs={12} md={8}>
        <KeyValue
          id="mobileNno"
          keyText={t('components->>ContactDetails.Mobile No.')}
          valueText={mobileVal}
        />
      </Grid>
      <Grid xs={12} md={8}>
        <KeyValue
          id="telNo"
          keyText={t('components:ContactDetails.Telephone No.')}
          valueText={phoneVal}
        />
      </Grid>
      <Grid xs={12} md={8}>
        <KeyValue
          id="mailingAddress"
          keyText={t('components->>ContactDetails.Residential Address')}
          valueText={mailingAddressVal}
        />
      </Grid>
      <Grid xs={12} md={8}>
        <KeyValue
          id="foreignAddress"
          keyText={t('components->>ContactDetails.Foreign Address')}
          valueText={foreignAddressVal}
        />
      </Grid>
    </>
  );
};

const ContactDetails = ({
  contactDetails,
  editable = false,
  onEdit = () => null,
  formHook,
  editMode,
}: ContactDetailsProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [isEditable, setIsEditable] = useState(false);

  /** handle Contact Detail Edit
   * */
  const onContactEditClick = useCallback(() => {
    setIsEditable(!isEditable);
    onEdit(!isEditable);
  }, [isEditable, onEdit]);

  useEffect(() => {
    if (editMode !== undefined) {
      setIsEditable(editMode);
    }
  }, [editMode]);

  const emailVal = useEmail(contactDetails);
  const phoneVal = usePhoneNumber(contactDetails);
  const mobileVal = useMobileNumber(contactDetails);
  const mailingAddressVal = useMailingAddress(contactDetails);
  const foreignAddressVal = useForeignAddress(contactDetails);

  return (<>
    <FormHeader
      title={t('components->>ContactDetails.Contact Details')}
      action={
        editable ? (
          /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
            <>{!isEditable && <Link onClick={onContactEditClick}>Edit</Link>}</>
        ) : null
      }
    />
    {!isEditable && (
      <NonEditableDetails
        emailVal={emailVal}
        phoneVal={phoneVal}
        mobileVal={mobileVal}
        mailingAddressVal={mailingAddressVal}
        foreignAddressVal={foreignAddressVal}
      />
    )}
    {isEditable && formHook && (
      <>
        <Grid xs={12} md={8}>
          <TextFieldForm
            className={classes.formElementContainer}
            label={t('components->>ContactDetails.Email')}
            fullWidth
            variant="outlined"
            id="email"
            name="email"
            title="Email"
            error={!!formHook?.formState.errors?.email}
            helperText={formHook?.formState.errors?.email?.message?.toString()}
            defaultValue={contactDetails?.email}
            form={formHook}
          />
        </Grid>
        <Grid xs={12} md={8}>
          <ContactNumberFormItem
            countryCodeRules={{
              required: { value: true, message: 'Please enter your country code.' },
            }}
            numberRegister={formHook?.register}
            countryCode={{
              name: 'mobileCountryCode',
              error: !!formHook?.formState.errors?.mobileCountryCode,
              helperText: formHook?.formState.errors?.mobileCountryCode?.message?.toString(),
            }}
            number={{
              name: 'mobileContactNumber',
              error: !!formHook?.formState.errors?.mobileContactNumber,
              helperText: formHook?.formState.errors?.mobileContactNumber?.message?.toString(),
            }}
            isSkipTransform
            containerClassName={classes.formElementContainer}
            formHook={formHook}
            countryCodeControl={formHook?.control}
            countryCodeDefaultValue={contactDetails?.mobileNumber?.countryCode}
            numberDefaultValue={contactDetails?.mobileNumber?.number}
            label={t('components->>ContactDetails.Mobile No.')}
          />
        </Grid>
        <Grid xs={12} md={8}>
          <ContactNumberFormItem
            containerClassName={classes.formElementContainer}
            formHook={formHook}
            countryCodeControl={formHook?.control}
            label={t('components->>ContactDetails.Telephone No.')}
            countryCodeDefaultValue={contactDetails?.phoneNumber?.countryCode}
            numberDefaultValue={contactDetails?.phoneNumber?.number}
            countryCodeRules={{
              required: { value: true, message: 'Please enter your country code.' },
            }}
            numberRegister={formHook?.register}
            countryCode={{
              name: 'telephoneCountryCode',
              error: !!formHook?.formState.errors?.telephoneCountryCode,
              helperText: formHook?.formState.errors?.telephoneCountryCode?.message?.toString(),
            }}
            number={{
              name: 'telephoneContactNumber',
              error: !!formHook?.formState.errors?.telephoneContactNumber,
              helperText: formHook?.formState.errors?.telephoneContactNumber?.message?.toString(),
            }}
            isSkipTransform
            placeholder="Enter Phone No."
          />
        </Grid>
        <Grid container xs={12} direction="column">
          <AddressEditSection formHook={formHook} contactDetails={contactDetails} />
        </Grid>
        <Grid xs={12} container className={classes.address}>
          <ForeignAddressEditSection formHook={formHook} contactDetails={contactDetails} />
        </Grid>
      </>
    )}
  </>);
};

export default ContactDetails;
